import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, Subject } from 'rxjs';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';

import { environment } from '../../../../environments/environment';
import {
  Airport,
  AppAnnounce,
  Country,
  ReselectFlightState,
} from '../../models/app';
import { AccountInfo } from '../../models/api/account';
import { APP_ROUTES, STORAGE_KEYS } from '../../constants';

import { AppStore } from '../../store';
import { LocalStorageService } from './local-storage.service';
import { BookingStore } from '../../../pages/booking/store';
import { FlightsData } from '../../models/api/flight';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private _showAnnounce$ = new Subject<AppAnnounce>();
  private _showReselect$ = new Subject<void>();
  private _localStorageService = inject(LocalStorageService);
  private _appStore = inject(AppStore);
  private _bookingStore = inject(BookingStore);
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _ngbModal = inject(NgbModal);
  private _ngbOffcanvas = inject(NgbOffcanvas);

  showAnnounce$ = this._showAnnounce$.asObservable();
  showReselect$ = this._showReselect$.asObservable();

  showAnnounce(data: AppAnnounce) {
    this._showAnnounce$.next(data);
  }

  showReselect() {
    this._showReselect$.next();
  }

  announceSessionExpired(isStatic: boolean = false) {
    this.showAnnounce({
      header: 'Thông báo',
      body: 'Phiên truy cập của bạn vừa hết hạn hoặc dữ liệu đã được làm mới. Bạn hãy thử lại nhé!',
      static: isStatic,
    });
  }

  redirectToReselectFlight(selectingFlights: FlightsData[]) {
    if (selectingFlights.length > 0) {
      const selectedFlights =
        this._bookingStore.reselectFlight.selectedFlights();

      const selectingFlight = selectingFlights.find((f) => f.leg === 0);
      if (selectingFlight) {
        selectedFlights?.push(selectingFlight);
        const selectedFlight = this._bookingStore.selectFlight.selectedFlight();
        if (selectedFlight) {
          selectedFlight.flights = [selectingFlight];
          this._bookingStore.updateSelectFlightState({
            selectedFlight: { ...selectedFlight },
          });
        }
      }

      const selectingRoundTripFlight = selectingFlights.find(
        (f) => f.leg === 1,
      );
      if (selectingRoundTripFlight) {
        selectedFlights?.push(selectingRoundTripFlight);
        const selectedRoundTripFlight =
          this._bookingStore.selectFlight.selectedRoundTripFlight();
        if (selectedRoundTripFlight) {
          selectedRoundTripFlight.flights = [selectingRoundTripFlight];
          this._bookingStore.updateSelectFlightState({
            selectedRoundTripFlight: { ...selectedRoundTripFlight },
          });
        }
      }

      this._bookingStore.updateReselectFlightState({ selectedFlights });

      const bookingRequest = this._bookingStore.bookingRequest();
      if (bookingRequest) {
        bookingRequest?.listPassenger.forEach((passenger) => {
          passenger.ancillaries = [];
        });
        this._bookingStore.updateBookingRequest(bookingRequest);
      }

      this._bookingStore.saveSession();
    }

    this._ngbModal.dismissAll();
    this._ngbOffcanvas.dismiss();
    this._router.navigateByUrl(APP_ROUTES.passengerInformation);
  }

  redirectToSearchSessionExpired() {
    const value = this._bookingStore.selectFlight();

    const queryParams = {
      adult: value.adult,
    } as {
      adult: number;
      child?: number;
      infant?: number;
      returnDate?: string;
    };
    if (value.child) queryParams.child = value.child;
    if (value.infant) queryParams.infant = value.infant;
    if (value.roundTrip) {
      queryParams.returnDate = dayjs(value.returnDate).format('DDMMYYYY');
    }

    let routeToRedirect = this._router.createUrlTree([APP_ROUTES.selectFlight]);
    if (value.from && value.to) {
      routeToRedirect = this._router.createUrlTree(
        [
          APP_ROUTES.selectFlight,
          value.from.code,
          value.to.code,
          dayjs(value.date).format('DDMMYYYY'),
        ],
        {
          queryParams,
        },
      );
    }

    this._ngbModal.dismissAll();
    this._ngbOffcanvas.dismiss();
    this._router.navigateByUrl(routeToRedirect);
  }

  getDefaultAirport(code: 'SGN' | 'HAN'): Airport {
    switch (code) {
      case 'SGN':
        return {
          code: 'SGN',
          city: 'Hồ Chí Minh',
          country: 'Việt Nam',
          img: '',
          search: '',
          keywords: [],
        };
      case 'HAN':
        return {
          code: 'HAN',
          city: 'Hà Nội',
          country: 'Việt Nam',
          img: '',
          search: '',
          keywords: [],
        };
    }
  }

  getAccountCode(): string {
    try {
      var localUser = this._localStorageService.getEncrypt<AccountInfo>(
        STORAGE_KEYS.USER,
      );
      if (localUser != null) {
        return localUser.accCode;
      }

      return '';
    } catch (err) {
      return '';
    }
  }

  getAgentCode(type: 'Booking' | 'ProcessBooking'): string {
    const defaultAgentCode = environment.defaultAgentCode;
    try {
      let agentCode = this._localStorageService.get<string>(
        STORAGE_KEYS.AGENT_CODE,
      );
      let accCode = this.getAccountCode();

      switch (type) {
        case 'Booking':
          if (agentCode) {
            return agentCode;
          }
          if (accCode) {
            return accCode;
          }
          break;
        case 'ProcessBooking':
          if (accCode) {
            return accCode;
          }
          if (agentCode) {
            return agentCode;
          }
          break;
      }

      return defaultAgentCode;
    } catch (err) {
      return defaultAgentCode;
    }
  }

  getAgentDomain(): string {
    const defaultAgentDomain = window.location.host;
    try {
      let agentDomain = this._localStorageService.get<string>(
        STORAGE_KEYS.AGENT_DOMAIN,
      );
      if (agentDomain) {
        return agentDomain;
      }
      return defaultAgentDomain;
    } catch (err) {
      return defaultAgentDomain;
    }
  }

  saveAgentCode(code: string) {
    this._localStorageService.set(STORAGE_KEYS.AGENT_CODE, code);
  }

  saveAgentDomain(domain: string) {
    this._localStorageService.set(STORAGE_KEYS.AGENT_DOMAIN, domain);
  }

  loadCountries() {
    return this._http
      .get<Array<Country>>(`json/nationality.json?v=${environment.version}`)
      .pipe(
        map((data) => {
          const vn = data.find((country) => country.alpha3 === 'VNM');
          if (vn) {
            data.splice(data.indexOf(vn), 1);
            data.unshift(vn);
          }

          this._appStore.updateCountries(data);
          return data;
        }),
      );
  }

  loadAirports() {
    return this._http
      .get<Array<Airport>>(`json/airports-full.json?v=${environment.version}`)
      .pipe(
        map((data) => {
          this._appStore.updateAirports(
            data.map((a) => ({
              ...a,
              keywords: a.search.split(' '),
            })),
          );
          return data;
        }),
      );
  }
}
